/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
/** @jsx jsx */

import { jsx } from '@emotion/react'
import { capitalize } from 'lodash'

import { EuiBadge, EuiFlexGroup, EuiFlexItem, EuiIcon } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import {
  getPrettyElasticsearchProjectSubtype,
  getPrettyObservabilityProjectSubtype,
  getPrettyProjectType,
} from '@modules/project-lib/display'
import type { AnyProject } from '@modules/project-api/types'
import { useFlagsWhenLoaded } from '@modules/launchdarkly'

const ProjectTypeIconAndLabel = ({ project }: { project: AnyProject }) => {
  const [_, flags] = useFlagsWhenLoaded()

  return (
    <EuiFlexGroup gutterSize='s' responsive={false} alignItems='center'>
      <EuiFlexItem grow={false}>
        <EuiIcon type={`logo${capitalize(project.type)}`} />
      </EuiFlexItem>
      <EuiFlexItem grow={false}>
        <EuiFlexGroup gutterSize='s' responsive={false} alignItems='baseline'>
          <EuiFlexItem grow={false}>
            <FormattedMessage {...getPrettyProjectType(project.type)} />
          </EuiFlexItem>

          {project.type === 'elasticsearch' && (
            <EuiFlexItem grow={false} css={{ maxWidth: '60%' }}>
              <EuiBadge>
                <FormattedMessage
                  {...getPrettyElasticsearchProjectSubtype(project.optimized_for)}
                />
              </EuiBadge>
            </EuiFlexItem>
          )}

          {project.type === 'observability' && flags.observabilityProjectTiers && (
            <EuiFlexItem grow={false} css={{ maxWidth: '60%' }}>
              <EuiBadge>
                <FormattedMessage {...getPrettyObservabilityProjectSubtype(project.product_tier)} />
              </EuiBadge>
            </EuiFlexItem>
          )}
        </EuiFlexGroup>
      </EuiFlexItem>
    </EuiFlexGroup>
  )
}

export default ProjectTypeIconAndLabel
